import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/apiClient';

/**
 * The one place a widget's data is fetched.
 *
 * Widgets call this and render the result; they never touch apiClient
 * directly. Two reasons that boundary is worth enforcing:
 *
 *  - Cache keys stay consistent, so two copies of the same widget with the
 *    same config share one request instead of racing.
 *  - A future third-party widget can be handed data without ever being handed
 *    an authenticated HTTP client. Given how much of this project went into
 *    tenant isolation, a plugin that could call any endpoint it liked would
 *    undo it.
 */

export interface UseWidgetDataOptions {
  /** Extra key parts — anything the request varies on (period, filters). */
  deps?: unknown[];
  /** Override the default 60s freshness for fast-moving data. */
  staleTime?: number;
  /** Poll interval in ms. Use sparingly — a live feed, not every tile. */
  refetchInterval?: number;
  enabled?: boolean;
}

export function useWidgetData<T = any>(
  widgetKey: string,
  endpoint: string | undefined,
  options: UseWidgetDataOptions = {}
) {
  const { deps = [], staleTime, refetchInterval, enabled = true } = options;

  const query = useQuery<T>({
    // widgetKey is part of the key even though the endpoint would usually be
    // unique: two widgets can legitimately read the same endpoint with
    // different post-processing, and they should not evict each other.
    queryKey: ['widget', widgetKey, endpoint, ...deps],
    queryFn: async () => {
      if (!endpoint) throw new Error('This widget has no data endpoint configured.');
      // apiClient already unwraps { status, data } for GET.
      const raw = await api.get<T>(stripApiPrefix(endpoint));
      return withCamelAliases(raw) as T;
    },
    enabled: enabled && Boolean(endpoint),
    staleTime,
    refetchInterval,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error as Error | undefined,
    refetch: query.refetch,
  };
}

/**
 * The registry stores absolute paths (`/api/v1/finance/...`) because the
 * server describes itself in absolute terms, but apiClient's baseURL already
 * ends in `/api/v1`. Sending the full path produces `/api/v1/api/v1/...`,
 * which 404s in a way that looks like a missing endpoint rather than a
 * doubled prefix.
 */
function stripApiPrefix(endpoint: string): string {
  return endpoint.replace(/^\/api\/v\d+/, '');
}

const toCamel = (key: string) => key.replace(/_([a-z0-9])/g, (_, c: string) => c.toUpperCase());

/**
 * Add a camelCase alias beside every snake_case key.
 *
 * The API is not consistent about casing and never will be retroactively: the
 * older modules return raw MySQL rows (`company_name`, `invoice_date`,
 * `total_amount`) while the newer ones hand-map to camelCase. A widget
 * therefore has to guess, and guessing wrong fails silently — the invoice list
 * rendered "Unknown customer" for every row purely because it read
 * `customerName` from a payload carrying `company_name`.
 *
 * Aliasing here fixes every widget at once and is additive: the original keys
 * are preserved, so nothing that already worked changes. The alternative —
 * auditing 34 widgets against 20 endpoints by hand — would have to be redone
 * each time an endpoint changes shape.
 */
function withCamelAliases(value: unknown, depth = 0): unknown {
  // Payloads are shallow; the bound just stops a cyclic structure looping.
  if (depth > 6 || value === null || typeof value !== 'object') return value;

  if (Array.isArray(value)) return value.map((v) => withCamelAliases(v, depth + 1));

  // Leave non-plain objects (Date, etc.) intact.
  if (Object.getPrototypeOf(value) !== Object.prototype) return value;

  const out: Record<string, unknown> = {};
  for (const [key, raw] of Object.entries(value as Record<string, unknown>)) {
    const mapped = withCamelAliases(raw, depth + 1);
    out[key] = mapped;
    const camel = toCamel(key);
    // Never clobber a key the server actually sent.
    if (camel !== key && !(camel in (value as object))) out[camel] = mapped;
  }
  return out;
}

export { withCamelAliases };

/** Invalidate every widget's cache — the dashboard-level "refresh all". */
export function useRefreshAllWidgets() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ['widget'] });
}

export { stripApiPrefix };
